import { type ReactNode, useCallback, useMemo } from "react";
import { EventGridListItemEventState, type EventGridListItemTagDescriptor } from "@stubhub/ensemble-discovery";
import { type EventListGridItem, GridType } from "src/http/types";
import { useTicketAvailability } from "src/website-apps/responsive/stubhub-performer/hooks/useTicketAvailabililty";
import { useEventAnalytics } from "src/website-apps/shared/components/common-event-grid-list-item/use-event-analytics";
import { useExtendMerchDedupingArm } from "src/website-apps/shared/hooks/useExtendMerchDedupingArm";
import { useMerchMessageTags } from "src/website-apps/shared/hooks/useMerchMessageTags";

interface Options {
  /** Event being rendered. */
  event: EventListGridItem;
  /** Whether the primary (page-context) team is the home team for this event. */
  isHomeGame: boolean;
  /** Position of this card amongst days-with-events in the calendar (analytics gridIndex). */
  calendarIndex: number;
  /** Handler to open the notification modal for sold-out events. */
  openNotificationHandler: (eventId: number) => void;
  /** Click event handler (forwarded with the event). */
  onClick: (event: EventListGridItem) => void;
  /** Mouse-enter event handler (forwarded with the event). */
  onMouseEnter: (event: EventListGridItem) => void;
}

interface Result {
  /** Whether tickets are currently available for this event. */
  available: boolean;
  /** Drives Ensemble's element type: Active → <a>, SoldOut → <button>. */
  eventState: EventGridListItemEventState;
  /** Merch tags resolved for this event (BestSelling included when applicable). */
  merchTags: EventGridListItemTagDescriptor[];
  /** In-view trackers from useMerchMessageTags — must be rendered alongside the tile. */
  inViewTrackers: ReactNode;
  /** Primary-team color (only set when this is a home game). */
  primaryColor: string | undefined;
  /** Click handler bound to the event — sends CLICK analytics + opens notification when sold out. */
  handleClick: () => void;
  /** Mouse-enter handler bound to the event. */
  handleMouseEnter: () => void;
  /** In-view handler bound to the event — sends batched VIEW analytics. */
  handleInView: () => void;
}

/**
 * Encapsulates per-event state shared by single-event date cards and multi-event
 * portions: ticket availability, merch tags, click/view analytics, sold-out
 * notification handoff, and primary-team theming.
 *
 * Sport-agnostic — relies only on the generic event shape. New sports get a
 * presentational component on top of this hook.
 *
 * Must be called only when there is a real event; callers that may render
 * empty/multi-event tiles should branch before invoking the hook so we don't
 * fire analytics or read availability for placeholder events.
 */
export const useDateCardEventState = ({
  event,
  isHomeGame,
  calendarIndex,
  openNotificationHandler,
  onClick,
  onMouseEnter,
}: Options): Result => {
  const ticketAvailabilityArray = useMemo(() => [event], [event]);
  const { eventAvailability } = useTicketAvailability(ticketAvailabilityArray);
  const available = !!eventAvailability[0];

  const { tags: merchTags, inViewTrackers } = useMerchMessageTags({
    event,
    disabled: !available,
    maxTags: 1,
  });

  const { variantExperimentsForUAE } = useExtendMerchDedupingArm({
    event,
    isExperimentCard: false,
  });
  const { fireClickPageEvents, fireHoverPageEvents, fireInViewPageEvents } = useEventAnalytics({
    event,
    gridType: GridType.Calendar,
    index: calendarIndex,
    isHighlightedItem: !!event.hottestEventMessage?.hasValue,
    merchTags,
    variantExperimentsForUAE,
    enableInViewEvents: true,
  });

  const handleInView = useCallback(() => {
    fireInViewPageEvents();
  }, [fireInViewPageEvents]);

  const handleClick = useCallback(() => {
    onClick(event);
    fireClickPageEvents();
    if (!available && event.eventId) {
      openNotificationHandler(event.eventId);
    }
  }, [onClick, event, available, openNotificationHandler, fireClickPageEvents]);

  const handleMouseEnter = useCallback(() => {
    onMouseEnter(event);
    fireHoverPageEvents();
  }, [onMouseEnter, event, fireHoverPageEvents]);

  const eventState = available ? EventGridListItemEventState.Active : EventGridListItemEventState.SoldOut;

  const primaryTeamMetadataKey = isHomeGame
    ? event.eventMetadata?.sports?.homeTeamId
    : event.eventMetadata?.sports?.awayTeamId;
  const primaryTeamMetadata = primaryTeamMetadataKey
    ? event.eventMetadata?.metadataForAllCategoriesInEvent?.[primaryTeamMetadataKey]
    : null;
  const primaryColor = isHomeGame ? (primaryTeamMetadata?.primaryColor ?? undefined) : undefined;

  return {
    available,
    eventState,
    merchTags,
    inViewTrackers,
    primaryColor,
    handleClick,
    handleMouseEnter,
    handleInView,
  };
};
